import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import type { UserInformation } from "../../../features/Login/typings/login.types";
import {
  getScheduleExternalMemberDisplayName,
  isScheduleBookedForExternalMember
} from "./schedule-visitor-display-name.function";
import { shouldShowScheduleVisitorDisplayName } from "../should-show-visitor-schedule-icon.function";

/**
 * Minimal user row used to paint photo/initials on calendar Show-place desks.
 * Matches colleague picker / TeamMember fields needed for occupancy.
 */
export type ScheduleFloorPlanOccupancyUser = {
  /** Auth subject; matches booking `userBookedFor`. */
  userId: string;
  firstName?: string;
  surname?: string;
  /** Profile image URL when the user has one; otherwise initials are used. */
  photoUrl?: string | null;
};

type OccupancyUserSource = {
  userId?: string;
  firstName?: string;
  surname?: string;
  photoUrl?: string | null;
};

function isOccupancyUser(user: OccupancyUserSource): user is ScheduleFloorPlanOccupancyUser {
  return Boolean(user.userId);
}

/**
 * Flattens picker / directory / all-users lists for Show-place occupancy lookup.
 * Duplicate `userId` rows are kept; resolve prefers the first entry that has a photo.
 */
export function mergeScheduleFloorPlanOccupancyUsers(
  sources: ReadonlyArray<readonly OccupancyUserSource[] | undefined>
): ScheduleFloorPlanOccupancyUser[] {
  return sources.flatMap(source => (source ?? []).filter(isOccupancyUser));
}

/**
 * Calendar Show-place directory: explicit occupancy list, then selected colleagues, then calendar users.
 */
export function pickScheduleFloorPlanOccupancyUsers(
  occupancyUsers?: readonly ScheduleFloorPlanOccupancyUser[],
  selectedColleagues?: readonly ScheduleFloorPlanOccupancyUser[],
  users?: readonly ScheduleFloorPlanOccupancyUser[]
): readonly ScheduleFloorPlanOccupancyUser[] | undefined {
  return occupancyUsers ?? selectedColleagues ?? users;
}

type OccupancyLoginUser = Pick<UserInformation, "sub" | "firstName" | "surname" | "photoUrl">;

type NameParts = {
  firstName: string;
  surname: string;
};

function occupancyIconFromPerson(
  firstName?: string,
  surname?: string,
  photoUrl?: string | null
): string | undefined {
  const photo = photoUrl?.trim();
  if (photo && photo.length > 2) {
    return photo;
  }

  const first = firstName?.trim();
  const last = surname?.trim();
  if (first && last) {
    return first[0].toUpperCase() + last[0].toUpperCase();
  }
  if (first) {
    return first[0].toUpperCase();
  }
  if (last) {
    return last[0].toUpperCase();
  }
  return undefined;
}

function namePartsFromDisplayName(displayName: string): NameParts | undefined {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return undefined;
  }
  if (parts.length === 1) {
    return { firstName: parts[0], surname: "" };
  }
  return {
    firstName: parts.slice(0, -1).join(" "),
    surname: parts[parts.length - 1]
  };
}

function occupancyIconFromDisplayName(displayName: string): string | undefined {
  const parts = namePartsFromDisplayName(displayName);
  if (!parts) {
    return undefined;
  }
  return occupancyIconFromPerson(parts.firstName, parts.surname);
}

function resolveVisitorOccupancyIcon(schedule: BookingScheduleInterface): string | undefined {
  const visitor = schedule.visitorBookings?.[0];
  const fromRegistration = occupancyIconFromPerson(
    visitor?.firstName ?? undefined,
    visitor?.surname ?? undefined
  );
  if (fromRegistration) {
    return fromRegistration;
  }
  return occupancyIconFromDisplayName(schedule.externalName?.trim() ?? "");
}

function resolveExternalOccupancyIcon(schedule: BookingScheduleInterface): string | undefined {
  return occupancyIconFromDisplayName(getScheduleExternalMemberDisplayName(schedule));
}

function resolveColleagueOccupancyIcon(
  schedule: BookingScheduleInterface,
  users: readonly ScheduleFloorPlanOccupancyUser[] | undefined
): string | undefined {
  const userId = schedule.userBookedFor;
  if (!userId) {
    return undefined;
  }
  const matches = (users ?? []).filter(user => user.userId === userId);
  const withPhoto = matches.find(user => (user.photoUrl?.trim().length ?? 0) > 2);
  const colleague = withPhoto ?? matches[0];
  if (!colleague) {
    return undefined;
  }
  return occupancyIconFromPerson(colleague.firstName, colleague.surname, colleague.photoUrl);
}

/**
 * Photo URL or initials for the highlighted desk on calendar / Show-place floor plans.
 * Uses the booking assignee (visitor, external guest, colleague, or logged-in user), not leftover wizard occupants.
 */
export function resolveScheduleFloorPlanOccupancyIcon({
  schedule,
  loginUser,
  users
}: {
  schedule: BookingScheduleInterface;
  loginUser: OccupancyLoginUser;
  users?: readonly ScheduleFloorPlanOccupancyUser[];
}): string | undefined {
  if (shouldShowScheduleVisitorDisplayName(schedule)) {
    const visitorIcon = resolveVisitorOccupancyIcon(schedule);
    if (visitorIcon) {
      return visitorIcon;
    }
  }

  if (isScheduleBookedForExternalMember(schedule)) {
    const externalIcon = resolveExternalOccupancyIcon(schedule);
    if (externalIcon) {
      return externalIcon;
    }
  }

  if (schedule.userBookedFor === loginUser.sub) {
    return occupancyIconFromPerson(loginUser.firstName, loginUser.surname, loginUser.photoUrl);
  }

  return resolveColleagueOccupancyIcon(schedule, users);
}
